import { useEffect, useState } from "react";
import {
  getAdminDebug,
  getAdminUser,
  fetchRequests,
  signOutAdmin,
  supabase,
  triggerLogin,
} from "./supabaseClient";
import SideBar from "./SideBar";
import PortfolioFooter from "./PortfolioFooter";


const styles = `
  *, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  :root {
    --bg: #f5f2ee;
    --surface: #ffffff;
    --surface-hover: #faf9f7;
    --border: #e8e3dc;
    --border-light: #f0ece5;
    --text-primary: #1a1714;
    --text-secondary: #6b6560;
    --text-muted: #a09890;
    --accent: #c8522a;
    --accent-light: #fdf0eb;
    --accent-mid: #f5d5c8;
    --tag-bg: #ede9e3;
    --tag-text: #5a5550;
    --highlight: #fff3a3;
  }

  body {
    font-family: "PP Neue Montreal", "Outfit", sans-serif;
    background: var(--bg);
    color: var(--text-primary);
    min-height: 100vh;
    padding: 0;
  }

  .admin-shell {
    min-height: 100vh;
    background: var(--bg);
  }

  .admin-layout {
    display: grid;
    grid-template-columns: 340px minmax(0, 1fr);
    align-items: start;
    min-height: 100vh;
  }

  .admin-main {
    min-width: 0;
  }

  .header {
    background: var(--surface);
    border-bottom: 1px solid var(--border);
    padding: 0 40px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 64px;
    position: sticky;
    top: 0;
    z-index: 60;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .header h1 {
    font-family: "PP Neue Montreal", "Outfit", sans-serif;
    font-size: 22px;
    font-weight: 400;
    color: var(--text-primary);
    letter-spacing: -0.3px;
  }

  .header-meta {
    font-size: 13px;
    color: var(--text-muted);
    font-weight: 300;
  }

  .badge {
    background: var(--accent);
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 20px;
    letter-spacing: 0.3px;
  }

  .toolbar {
    padding: 24px 40px 0;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .toolbar-row {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .search-wrap {
    position: relative;
    flex: 1;
    min-width: 240px;
  }

  .search-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-muted);
    font-size: 15px;
    pointer-events: none;
  }

  .search-input {
    width: 100%;
    padding: 10px 14px 10px 38px;
    border: 1.5px solid var(--border);
    border-radius: 10px;
    font-size: 14px;
    font-family: "PP Neue Montreal", "Outfit", sans-serif;
    background: var(--surface);
    color: var(--text-primary);
    transition: border-color 0.18s, box-shadow 0.18s;
    outline: none;
  }

  .search-input::placeholder {
    color: var(--text-muted);
  }

  .search-input:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--accent-light);
  }

  .filter-chips {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    align-items: center;
  }

  .chip-label {
    font-size: 12px;
    color: var(--text-muted);
    font-weight: 500;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    margin-right: 2px;
  }

  .chip {
    padding: 6px 14px;
    border-radius: 20px;
    border: 1.5px solid var(--border);
    background: var(--surface);
    font-size: 13px;
    font-family: "PP Neue Montreal", "Outfit", sans-serif;
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.15s;
    font-weight: 400;
  }

  .chip:hover {
    border-color: var(--accent);
    color: var(--accent);
  }

  .chip.active {
    background: var(--accent);
    border-color: var(--accent);
    color: #fff;
    font-weight: 500;
  }

  .sort-select {
    padding: 8px 14px;
    border: 1.5px solid var(--border);
    border-radius: 10px;
    font-size: 13px;
    font-family: "PP Neue Montreal", "Outfit", sans-serif;
    background: var(--surface);
    color: var(--text-secondary);
    cursor: pointer;
    outline: none;
    transition: border-color 0.15s;
  }

  .sort-select:focus {
    border-color: var(--accent);
  }

  .display-editor {
    background: var(--surface);
    border: 1.5px solid var(--border);
    border-radius: 14px;
    padding: 18px 20px;
    margin: 16px 40px 0;
    display: none;
  }

  .display-editor.open {
    display: block;
  }

  .editor-title {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: var(--text-muted);
    margin-bottom: 14px;
  }

  .editor-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 12px;
  }

  .editor-group label {
    display: block;
    font-size: 12px;
    color: var(--text-secondary);
    font-weight: 500;
    margin-bottom: 5px;
  }

  .editor-group input,
  .editor-group select {
    width: 100%;
    padding: 7px 10px;
    border: 1.5px solid var(--border);
    border-radius: 8px;
    font-size: 13px;
    font-family: "PP Neue Montreal", "Outfit", sans-serif;
    background: var(--bg);
    color: var(--text-primary);
    outline: none;
  }

  .editor-group input:focus,
  .editor-group select:focus {
    border-color: var(--accent);
  }

  .toggle-row {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
    margin-top: 12px;
  }

  .toggle-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--text-secondary);
    cursor: pointer;
  }

  .toggle-item input[type="checkbox"] {
    accent-color: var(--accent);
    width: 15px;
    height: 15px;
  }

  .edit-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border: 1.5px solid var(--border);
    border-radius: 10px;
    background: var(--surface);
    color: var(--text-secondary);
    font-size: 13px;
    font-family: "PP Neue Montreal", "Outfit", sans-serif;
    cursor: pointer;
    transition: all 0.15s;
    font-weight: 500;
  }

  .edit-btn:hover,
  .edit-btn.active {
    border-color: var(--accent);
    color: var(--accent);
    background: var(--accent-light);
  }

  .content {
    padding: 24px 40px 40px;
  }

  .results-info {
    font-size: 13px;
    color: var(--text-muted);
    margin-bottom: 16px;
    font-weight: 400;
  }

  .results-info span {
    color: var(--text-primary);
    font-weight: 600;
  }

  .cards-col {
    display: flex;
    flex-direction: column;
    gap: 14px;
    max-width: 780px;
  }

  .card {
    background: var(--surface);
    border: 1.5px solid var(--border);
    border-radius: 14px;
    padding: 22px 24px;
    transition: box-shadow 0.18s, border-color 0.18s, transform 0.18s;
    position: relative;
    overflow: hidden;
    animation: fadeSlide 0.3s ease both;
  }

  @keyframes fadeSlide {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .card:hover {
    border-color: var(--accent-mid);
    box-shadow: 0 4px 20px rgba(200, 82, 42, 0.07);
    transform: translateY(-1px);
  }

  .card-stripe {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: var(--accent);
    opacity: 0;
    transition: opacity 0.18s;
    border-radius: 3px 0 0 3px;
  }

  .card:hover .card-stripe {
    opacity: 1;
  }

  .card-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 12px;
    gap: 12px;
  }

  .avatar {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: var(--accent-light);
    border: 1.5px solid var(--accent-mid);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 600;
    color: var(--accent);
    font-family: "PP SUPPLY MONO", monospace;
    flex-shrink: 0;
  }

  .card-meta {
    flex: 1;
  }

  .card-email {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    letter-spacing: -0.2px;
    cursor: pointer;
    transition: color 0.15s;
  }

  .card-email:hover {
    color: var(--accent);
  }

  .card-date {
    font-size: 12px;
    color: var(--text-muted);
    margin-top: 2px;
  }

  .card-actions {
    display: flex;
    gap: 6px;
    opacity: 0;
    transition: opacity 0.15s;
  }

  .card:hover .card-actions {
    opacity: 1;
  }

  .action-btn {
    width: 30px;
    height: 30px;
    border: 1.5px solid var(--border);
    border-radius: 8px;
    background: var(--surface);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    color: var(--text-muted);
    transition: all 0.15s;
  }

  .action-btn:hover {
    border-color: var(--accent);
    color: var(--accent);
    background: var(--accent-light);
  }

  .card-message {
    font-size: 14px;
    line-height: 1.65;
    color: var(--text-secondary);
    font-weight: 300;
  }

  .card-message mark {
    background: var(--highlight);
    color: var(--text-primary);
    border-radius: 3px;
    padding: 0 2px;
  }

  .card-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 14px;
  }

  .tag {
    font-size: 11px;
    font-weight: 500;
    padding: 3px 10px;
    border-radius: 20px;
    background: var(--tag-bg);
    color: var(--tag-text);
    letter-spacing: 0.2px;
  }

  .tag-client {
    background: #e3f0fd;
    color: #2060b9;
  }

  .tag-collaborator {
    background: #e3fde8;
    color: #208040;
  }

  .tag-recruiter {
    background: #fdf0eb;
    color: #c8522a;
  }

  .tag-referral {
    background: #f0e3fd;
    color: #6020b9;
  }

  .tag-other {
    background: #ede9e3;
    color: #5a5550;
  }

  .empty {
    text-align: center;
    padding: 80px 40px;
    color: var(--text-muted);
  }

  .empty-icon {
    font-size: 40px;
    margin-bottom: 12px;
    opacity: 0.5;
  }

  .empty h3 {
    font-family: "Instrument Serif", serif;
    font-size: 22px;
    font-weight: 400;
    color: var(--text-secondary);
  }

  .empty p {
    font-size: 14px;
    margin-top: 6px;
  }

  .admin-footer-frame {
    background: #121212;
  }

  @media (max-width: 900px) {
    .admin-layout {
      display: block;
    }

    .header {
      padding: 0 18px 0 54px;
    }

    .toolbar {
      padding: 24px 18px 0 54px;
    }

    .display-editor {
      margin: 16px 18px 0 54px;
    }

    .content {
      padding: 24px 18px 40px 54px;
    }

    .cards-col {
      max-width: none;
    }
  }

  @media (max-width: 560px) {
    .header h1 {
      font-size: 18px;
    }

    .toolbar-row {
      align-items: stretch;
    }

    .search-wrap,
    .sort-select,
    .edit-btn {
      width: 100%;
    }

    .card {
      padding: 18px;
    }

    .card-actions {
      opacity: 1;
    }
  }
`;

const AdminPage = () => {
const [user, setUser] = useState(null);
const [debug, setDebug] = useState(null);
const [requests, setRequests] = useState([]);
const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [activeChip, setActiveChip] = useState("all");
  const [showEditor, setShowEditor] = useState(false);
  const [displayConfig, setDisplayConfig] = useState({
    fontSize: 14,
    maxLines: 0,
    dateFmt: "relative",
    showAvatar: true,
    showDate: true,
    showTags: true,
    highlight: true,
    accentColor: "#c8522a",
  });

  const detectTags = (message = "") => {
    const tags = [];
    const keywords = {
      client: ["client", "potential client", "business"],
      collaborator: ["collaborator", "partner", "partnership"],
      recruiter: ["recruiter", "recruitment", "hiring"],
      referral: ["referred", "referral", "recommendation"],
    };

    for (const [tag, words] of Object.entries(keywords)) {
      if (words.some((word) => message.toLowerCase().includes(word))) {
        tags.push(tag);
      }
    }

    return tags.length > 0 ? tags : ["other"];
  };

  const generateEmailTemplate = (request) => {
    const { email, message } = request;
    const tags = detectTags(message);

    let subject = "Re: Your Request";
    let body = "";

    if (tags.includes("client")) {
      subject = "Welcome - Let's Discuss Your Project";
      body = `Hi ${email.split("@")[0]},

Thank you for reaching out! I'm excited to hear about your project.

I wanted to follow up on your request:

"${message}"

I'd love to schedule a quick call to understand your needs better and discuss how I can help.

Are you available for a 15-minute call this week?

Best regards,`;
    } else if (tags.includes("collaborator")) {
      subject = "Collaboration Opportunity";
      body = `Hi ${email.split("@")[0]},

Thanks for your interest in collaborating! I think there's great potential here.

Regarding your message:

"${message}"

Let's sync up and explore how we can work together.

Cheers,`;
    } else if (tags.includes("recruiter")) {
      subject = "Re: Opportunity";
      body = `Hi ${email.split("@")[0]},

Thanks for thinking of me! I appreciate the opportunity.

I read your message:

"${message}"

I'm always open to interesting opportunities. Could you share more details about the role?

Best,`;
    } else if (tags.includes("referral")) {
      subject = "Thanks for the Referral";
      body = `Hi ${email.split("@")[0]},

Thank you so much for the referral! I really appreciate you thinking of me.

Your message:

"${message}"

I'd love to connect and learn more.

Regards,`;
    } else {
      subject = "Thank you for your message";
      body = `Hi ${email.split("@")[0]},

Thank you for reaching out!

I received your message:

"${message}"

I'll get back to you shortly.

Best regards,`;
    }

    return { subject, body };
  };

  const handleEmailClick = (request) => {
    const { subject, body } = generateEmailTemplate(request);
    window.location.href = `mailto:${request.email}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this request?");
    if (!confirmDelete) return;

    try {
      const { error } = await supabase
        .from("access_requests")
        .delete()
        .eq("id", id);

      if (error) throw error;

      setRequests((prev) => prev.filter((request) => request.id !== id));
    } catch (err) {
      console.error("Delete failed:", err.message);
    }
  };

useEffect(() => {
  let mounted = true;
  let subscription;

  const init = async () => {
    const info = await getAdminDebug();
    if (!mounted) return;
    setDebug(info);

    const admin = await getAdminUser();
    if (!mounted) return;

    if (!admin) {
      setUser(null);
      setRequests([]);
      setLoading(false);
      return;
    }

    setUser(admin);

    const data = await fetchRequests();
    if (!mounted) return;

    setRequests(data);
    setLoading(false);
  };

  init();

  if (supabase) {
    const { data } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!mounted) return;

      if (session?.user) {
        setLoading(true);
        await init();
        return;
      }

      setDebug((current) =>
        current
          ? { ...current, sessionEmail: "", authorized: false }
          : current
      );
      setUser(null);
      setRequests([]);
      setLoading(false);
    });

    subscription = data.subscription;
  }

  return () => {
    mounted = false;
    subscription?.unsubscribe();
  };
}, []);


  const getInitials = (email = "") => {
    const name = email.split("@")[0] || "NA";
    const parts = name.split(/[._-]/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  const formatDate = (iso, fmt) => {
    const d = new Date(iso);
    const now = new Date();

    if (fmt === "relative") {
      const diff = Math.floor((now - d) / 1000);
      if (diff < 60) return "just now";
      if (diff < 3600) return Math.floor(diff / 60) + " min ago";
      if (diff < 86400) return Math.floor(diff / 3600) + "h ago";

      const days = Math.floor(diff / 86400);
      if (days < 7) return days + " day" + (days > 1 ? "s" : "") + " ago";

      return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    }

    if (fmt === "short") {
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    }

    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const highlightText = (text = "", query) => {
    if (!query || !displayConfig.highlight) return text;

    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const parts = text.split(new RegExp(`(${escaped})`, "gi"));

    return parts.map((part, index) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={index}>{part}</mark>
      ) : (
        part
      )
    );
  };

  const tagClass = {
    client: "tag-client",
    collaborator: "tag-collaborator",
    recruiter: "tag-recruiter",
    referral: "tag-referral",
    other: "tag-other",
  };

  const tagLabel = {
    client: "Client",
    collaborator: "Collaborator",
    recruiter: "Recruiter",
    referral: "Referral",
    other: "Other",
  };

  const filteredRequests = requests
    .filter((request) => {
      const message = request.message || "";
      const email = request.email || "";
      const tags = detectTags(message);
      const matchChip = activeChip === "all" || tags.includes(activeChip);
      const matchSearch =
        !searchTerm ||
        message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        email.toLowerCase().includes(searchTerm.toLowerCase());

      return matchChip && matchSearch;
    })
    .sort((a, b) => {
      if (sortBy === "newest") {
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }

      if (sortBy === "oldest") {
        return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      }

      if (sortBy === "az") {
        return (a.email || "").localeCompare(b.email || "");
      }

      return 0;
    });

  const urgentCount = requests.filter((request) =>
    detectTags(request.message || "").includes("client")
  ).length;

  if (loading) {
    return (
      <div className="admin-shell">
        <style>{styles}</style>
        <div className="admin-layout">
          <SideBar />
          <main className="admin-main">
            <div style={{ display: "grid", placeItems: "center", minHeight: "100vh" }}>
              <p>Loading...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

if (!user) {
  return (
    <div className="admin-shell">
      <style>{styles}</style>
      <div className="admin-layout">
        <SideBar />
        <main className="admin-main">
          <div
            style={{
              display: "grid",
              placeItems: "center",
              minHeight: "100vh",
              padding: "24px",
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: "640px",
                background: "#fff",
                border: "1px solid #e8e3dc",
                borderRadius: "16px",
                padding: "24px",
              }}
            >
              <h2 style={{ marginBottom: "10px" }}>Not authorized</h2>
              <p style={{ color: "#6b6560", marginBottom: "16px" }}>
                This page only opens for the configured Google admin account.
              </p>

              {debug?.sessionEmail ? (
                <p style={{ color: "#6b6560", marginBottom: "16px" }}>
                  Signed in as: <strong>{debug.sessionEmail}</strong>
                </p>
              ) : (
                <p style={{ color: "#6b6560", marginBottom: "16px" }}>
                  No admin session is active yet.
                </p>
              )}

              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "18px" }}>
                <button
                  type="button"
                  onClick={() => triggerLogin()}
                  style={{
                    padding: "10px 14px",
                    borderRadius: "10px",
                    border: "1px solid #c8522a",
                    background: "#c8522a",
                    color: "#fff",
                    cursor: "pointer",
                  }}
                >
                  Sign in with Google
                </button>

                <button
                  type="button"
                  onClick={async () => {
                    await signOutAdmin();
                    window.location.reload();
                  }}
                  style={{
                    padding: "10px 14px",
                    borderRadius: "10px",
                    border: "1px solid #d6d0c8",
                    background: "#fff",
                    color: "#1a1714",
                    cursor: "pointer",
                  }}
                >
                  Sign out and retry
                </button>
              </div>

              <div
                style={{
                  background: "#f8f5f1",
                  border: "1px solid #e8e3dc",
                  borderRadius: "12px",
                  padding: "16px",
                  fontSize: "13px",
                  lineHeight: 1.7,
                  color: "#2b2622",
                  overflowX: "auto",
                  whiteSpace: "pre-wrap",
                }}
              >
                Expected admin email: {debug?.adminEmail || "not configured"}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}


  return (
    <div className="admin-shell">
      <style>{styles}</style>

      <div className="admin-layout">
        <SideBar />

        <main className="admin-main">
          <div className="header">
            <div className="header-left">
              <h1>Notifications</h1>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <span className="header-meta">{filteredRequests.length} requests</span>
              <span className="badge">{urgentCount}</span>
            </div>
          </div>

          <div className="toolbar">
            <div className="toolbar-row">
              <div className="search-wrap">
                <span className="search-icon">⌕</span>
                <input
                  className="search-input"
                  type="text"
                  placeholder="Filter by keyword..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <select
                className="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="az">A → Z email</option>
              </select>

              <button
                className={`edit-btn ${showEditor ? "active" : ""}`}
                onClick={() => setShowEditor(!showEditor)}
                type="button"
              >
                Display Options
              </button>
            </div>

            <div className="toolbar-row filter-chips">
              <span className="chip-label">Filter:</span>

              {["all", "client", "collaborator", "recruiter", "referral", "other"].map(
                (chip) => (
                  <button
                    key={chip}
                    className={`chip ${activeChip === chip ? "active" : ""}`}
                    onClick={() => setActiveChip(chip)}
                    type="button"
                  >
                    {chip === "all" ? "All" : tagLabel[chip]}
                  </button>
                )
              )}
            </div>
          </div>

          <div className={`display-editor ${showEditor ? "open" : ""}`}>
            <div className="editor-title">Card Display Settings</div>

            <div className="editor-grid">
              <div className="editor-group">
                <label>Message font size px</label>
                <input
                  type="number"
                  value={displayConfig.fontSize}
                  min="10"
                  max="24"
                  onChange={(e) =>
                    setDisplayConfig({
                      ...displayConfig,
                      fontSize: parseInt(e.target.value, 10) || 14,
                    })
                  }
                />
              </div>

              <div className="editor-group">
                <label>Max message lines 0 = all</label>
                <input
                  type="number"
                  value={displayConfig.maxLines}
                  min="0"
                  max="20"
                  onChange={(e) =>
                    setDisplayConfig({
                      ...displayConfig,
                      maxLines: parseInt(e.target.value, 10) || 0,
                    })
                  }
                />
              </div>

              <div className="editor-group">
                <label>Date format</label>
                <select
                  value={displayConfig.dateFmt}
                  onChange={(e) =>
                    setDisplayConfig({ ...displayConfig, dateFmt: e.target.value })
                  }
                >
                  <option value="relative">Relative</option>
                  <option value="short">Short</option>
                  <option value="full">Full date</option>
                </select>
              </div>

              <div className="editor-group">
                <label>Card accent</label>
                <input
                  type="color"
                  value={displayConfig.accentColor}
                  onChange={(e) => {
                    const color = e.target.value;
                    setDisplayConfig({ ...displayConfig, accentColor: color });
                    document.documentElement.style.setProperty("--accent", color);
                  }}
                />
              </div>
            </div>

            <div className="toggle-row">
              <label className="toggle-item">
                <input
                  type="checkbox"
                  checked={displayConfig.showAvatar}
                  onChange={(e) =>
                    setDisplayConfig({
                      ...displayConfig,
                      showAvatar: e.target.checked,
                    })
                  }
                />
                Show avatar
              </label>

              <label className="toggle-item">
                <input
                  type="checkbox"
                  checked={displayConfig.showDate}
                  onChange={(e) =>
                    setDisplayConfig({
                      ...displayConfig,
                      showDate: e.target.checked,
                    })
                  }
                />
                Show date
              </label>

              <label className="toggle-item">
                <input
                  type="checkbox"
                  checked={displayConfig.showTags}
                  onChange={(e) =>
                    setDisplayConfig({
                      ...displayConfig,
                      showTags: e.target.checked,
                    })
                  }
                />
                Show tags
              </label>

              <label className="toggle-item">
                <input
                  type="checkbox"
                  checked={displayConfig.highlight}
                  onChange={(e) =>
                    setDisplayConfig({
                      ...displayConfig,
                      highlight: e.target.checked,
                    })
                  }
                />
                Highlight matches
              </label>
            </div>
          </div>

          <div className="content">
            <p className="results-info">
              Showing <span>{filteredRequests.length}</span> of{" "}
              <span>{requests.length}</span> requests
              {searchTerm && (
                <>
                  {" "}
                  matching "<em>{searchTerm}</em>"
                </>
              )}
            </p>

            {filteredRequests.length === 0 ? (
              <div className="empty">
                <div className="empty-icon">○</div>
                <h3>No requests found</h3>
                <p>Try a different keyword or filter</p>
              </div>
            ) : (
              <div className="cards-col">
                {filteredRequests.map((request, index) => {
                  const initials = getInitials(request.email);
                  const dateStr = formatDate(
                    request.created_at || new Date().toISOString(),
                    displayConfig.dateFmt
                  );

                  const msgStyle = {
                    fontSize: `${displayConfig.fontSize}px`,
                    ...(displayConfig.maxLines > 0 && {
                      display: "-webkit-box",
                      WebkitLineClamp: displayConfig.maxLines,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }),
                  };

                  return (
                    <div
                      key={request.id}
                      className="card"
                      style={{ animationDelay: `${index * 40}ms` }}
                    >
                      <div className="card-stripe"></div>

                      <div className="card-header">
                        <div
                          style={{
                            display: "flex",
                            gap: "12px",
                            alignItems: "flex-start",
                            flex: 1,
                          }}
                        >
                          {displayConfig.showAvatar && (
                            <div className="avatar">{initials}</div>
                          )}

                          <div className="card-meta">
                            <div
                              className="card-email"
                              onClick={() => handleEmailClick(request)}
                              title="Click to reply with template"
                            >
                              {highlightText(request.email, searchTerm)}
                            </div>

                            {displayConfig.showDate && (
                              <div className="card-date">{dateStr}</div>
                            )}
                          </div>
                        </div>

                        <div className="card-actions">
                          <button
                            className="action-btn"
                            title="Delete"
                            onClick={() => handleDelete(request.id)}
                            type="button"
                          >
                            ×
                          </button>
                        </div>
                      </div>

                      <div className="card-message" style={msgStyle}>
                        {highlightText(request.message, searchTerm)}
                      </div>

                      {displayConfig.showTags && (
                        <div className="card-tags">
                          {detectTags(request.message).map((tag) => (
                            <span
                              key={tag}
                              className={`tag ${tagClass[tag] || ""}`}
                            >
                              {tagLabel[tag] || tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>

      <div className="frame6 admin-footer-frame" data-platform>
        <PortfolioFooter />
      </div>
    </div>
  );
};

export default AdminPage;





















